import { sortBy, uniqBy } from "lodash";
import React, { ComponentProps, useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { SearchContext } from "@examspark/common-app-search";
import { VSpace } from "@examspark/common-ui";
import { Course } from "@examspark/types";

import {
  useQueriesGetCourses,
  useQueryGetCoursesBySubject,
  useQueryGetCourseUnits,
} from "../../api/operations/course";
import { useQueriesGetUnits } from "../../api/operations/unit";
import { ActivityEditorConfigContext } from "../activities/activity-editor/ActivityEditor";

import FacetCheckboxFilter from "./FacetCheckboxFilter";

type StyledProps = {
  isLoading?: boolean;
};
const Styled = styled.div<StyledProps>``;

const StyledNoResults = styled.div`
  color: #777;
  font-size: 1rem;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;
  font-size: inherit;
  font-family: inherit;
`;

const FilterUnits = (props: ComponentProps<typeof FacetCheckboxFilter>) => {
  const search = useContext(SearchContext);
  const config = useContext(ActivityEditorConfigContext);
  const thisConfig = config.itemPicker?.filters?.unit;
  const [courseId, setCourseId] = useState<string>("");

  /**
   * Typically the course `select` depends on subject being filtered, but this
   * doesn't work in a non K-12 context where subject isn't provided. This
   * allows you to BYO course Ids to display as options (for G-W).
   */
  const configCourses = thisConfig?.courseIds;

  /** For resolving unit IDs to human-readable names */

  //
  // Make sure that we always show selected facets, even if they don't exist
  // in the provided options list
  //
  const options = uniqBy(
    props.options.concat(
      ...props.selected.map((value) => ({
        value,
        count: "0",
        label: value,
      })),
    ),
    (f) => f.value,
  );

  /** For resolving config-provided course IDs to human-readable names */
  const courseQueries = useQueriesGetCourses(configCourses ?? []);

  const subject = search?.state.filter.subject?.[0];
  const courseBySubjectQuery = useQueryGetCoursesBySubject(subject);
  const courseUnitsQuery = useQueryGetCourseUnits(courseId);

  // we need to fetch the already-selected units in case they
  // are outside the course and thus need a name override
  const selectedUnitsOutsideCourseQuery = useQueriesGetUnits(
    props.selected
      .map((id) => ({
        id: id,
        sk: "unit#v0",
      }))
      .filter(
        (f) => courseUnitsQuery.data?.find((cu) => cu.id === f.id) == null,
      ),
  );

  const courseOptions: Course[] =
    configCourses != null
      ? configCourses.map((c) => {
          const _c = courseQueries.find((q) => q.data?.id === c);
          return {
            id: c,
            sk: "course#v0",
            name: _c?.data?.name ?? "",
            workspaceId: _c?.data?.workspaceId ?? "",
            schemaVersion: "2020-01-01",
          };
        })
      : (courseBySubjectQuery.data ?? []);

  useEffect(() => {
    if (configCourses?.length === 1) {
      setCourseId(configCourses[0]);
    }
  }, [configCourses]);

  const courseUnitIds = courseUnitsQuery.data?.map((item) => item.id) ?? [];

  useEffect(() => {
    if (!courseId) return;
    // console.log("units", courseUnitsQuery.data);
    search?.dispatch({
      type: "UPDATE",
      data: {
        referencesIdsUnitCourseUnits: courseUnitIds,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, courseUnitsQuery.data]);

  const filteredOptions = options.filter(
    (opt) =>
      //
      // Show the units for the course that is selected, if we have met the criteria
      // for the filter to be shown at all
      //
      ((subject != null || configCourses != null) &&
        !!courseUnitsQuery.data?.find((cu) => cu.id === opt.value)) ||
      //
      // Always show actually-selected units
      //
      props.selected.includes(opt.value),
  );

  const unitLabelOverrides =
    courseUnitsQuery.data?.reduce(
      (acc, unit) => ({
        ...acc,
        [unit.id]: unit.name ?? "",
      }),
      {} as Record<string, string>,
    ) ?? {};

  // Add the units that are outside the course to the label overrides
  props.selected.forEach((unitId) => {
    const unit = selectedUnitsOutsideCourseQuery.find(
      (q) => q.data?.id === unitId,
    )?.data;
    if (unit) {
      unitLabelOverrides[unitId] = unit.name ?? "";
    }
  });

  return (
    <FacetCheckboxFilter
      {...props}
      hide={!subject && !configCourses && props.selected.length === 0}
      options={filteredOptions}
      labelOverrides={unitLabelOverrides}
      sortFn={(options) => {
        return options.sort(
          (a, b) =>
            courseUnitIds.indexOf(a.value) - courseUnitIds.indexOf(b.value),
        );
      }}
    >
      <Styled isLoading={courseUnitsQuery.isFetching}>
        <VSpace size="0.5rem">
          <StyledSelect
            value={courseId}
            onChange={async (e) => {
              setCourseId(e.target.value);
            }}
            disabled={courseUnitsQuery.isFetching}
          >
            <option value="">
              {thisConfig?.courseSelectPlaceholder ?? "Filter by Course..."}
            </option>
            {sortBy(courseOptions, ["name"])?.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </StyledSelect>
          {!!courseId &&
            !courseUnitsQuery.isFetching &&
            !filteredOptions.length && (
              <StyledNoResults>
                {thisConfig?.noUnitResultsLabel ?? "No Units To List"}
              </StyledNoResults>
            )}
        </VSpace>
      </Styled>
    </FacetCheckboxFilter>
  );
};

export default FilterUnits;
